/**
 * The BAKE door — ADR-028 §2's first call site, forced into existence by the
 * export truth (Codex P1, #103): once crease-band vertices are weighted to a
 * helper, every artifact that carries clips MUST carry the helper's motion,
 * or a re-imported GLB / external player plays reweighted flesh against a
 * bone frozen at bind — elbows worse than the disease. So every exported
 * clip is run through the SAME evaluator the live door drives: per driver
 * keyframe, θ is read off the played local rotation and the helper's track
 * is emitted at factor·θ about the measured hinge axis. One evaluator, two
 * doors, zero duplicated math (§2's law, kept).
 *
 * E1's half-rotation track is dimensionless rotation — family-safe by
 * construction, the one corrective §2 promises as a native track.
 *
 * Depends on: ir/clip-data, correctives (evaluator + ResolvedHinge),
 * ir/rig-descriptor, math. Depended on by: the app's export door and the
 * bulk-bake harness.
 */
import type { ClipData, ClipTrack } from '../ir/clip-data.js';
import type { RigDescriptor } from '../ir/rig-descriptor.js';
import { KernelError } from '../ir/errors.js';
import { quatMultiply, quatNormalize, quatSlerp, type Quat } from '../math/quat.js';
import { evaluateHingeCorrectives, hingeBendAngle } from './hinge-correctives.js';
import type { ResolvedHinge } from './graft-hinge-helpers.js';

export class HingeBakeError extends KernelError {
  constructor(message: string) {
    super(`Cannot bake hinge tracks: ${message}`);
  }
}

export interface BakeHingeOptions {
  /**
   * E2 leaf TRANSLATION tracks are character-scaled millimetres — valid ONLY
   * in per-character artifacts (the app's own exports, where the clip's
   * anchor IS the model). The FAMILY artifact (bulk harness) must never carry
   * them (§6: one character's swell baked into every girth); it passes false
   * and ships rotation-only, which E1's dimensionless track satisfies.
   */
  readonly includeLeafTranslations: boolean;
}

/**
 * Append helper rotation tracks — and, per-character only, leaf translation
 * tracks — for every hinge whose driver the clip animates. A clip that
 * animates no driver returns UNCHANGED (same reference): the driver rests, so
 * the helper's static rest TRS is already the truth. Refusals (§7): an
 * additive clip (its rotations are deltas, not the absolute locals θ is read
 * from) and a clip that already carries a corrective track (double-baking
 * would stack corrections).
 */
export function bakeHingeTracks(
  clip: ClipData,
  hinges: readonly ResolvedHinge[],
  rig: RigDescriptor,
  opts: BakeHingeOptions,
): ClipData {
  if (hinges.length === 0) return clip;
  const byName = new Map(rig.bones.map((b) => [b.name, b] as const));
  const added: ClipTrack[] = [];
  for (const hinge of hinges) {
    for (const name of [hinge.helper, ...hinge.leaves.map((l) => l.name)]) {
      if (clip.tracks.some((t) => t.target === name)) {
        throw new HingeBakeError(
          `clip "${clip.name}" already carries a track for "${name}" — double-baking stacks corrections`,
        );
      }
    }
    const driverTrack = clip.tracks.find((t) => t.target === hinge.profile.driver && t.path === 'rotation');
    if (driverTrack === undefined) continue; // driver rests → helper's rest TRS is the truth
    if (clip.additive) {
      throw new HingeBakeError(
        `clip "${clip.name}" is additive — its rotations are deltas, and θ can only be read off absolute locals`,
      );
    }
    const driverBone = byName.get(hinge.profile.driver);
    const helperBone = byName.get(hinge.helper);
    if (driverBone === undefined || helperBone === undefined) {
      throw new HingeBakeError(
        `rig lacks "${driverBone === undefined ? hinge.profile.driver : hinge.helper}" — the resolved hinge and the rig disagree`,
      );
    }
    const driverBind = driverBone.localBind.rotation;
    const helperBind = helperBone.localBind.rotation;
    // RESAMPLE AT THE CLIP TIMEBASE, never only at the driver's keys (Codex
    // P2, #103 round four): the engine slerps the DRIVER between sparse keys
    // and the live door evaluates θ per rendered frame — but a helper track
    // keyed only at the driver's keys is itself slerped by the engine, and
    // slerp does not commute with the nonlinear θ extraction (near a 180°
    // branch the divergence is dramatic). Sampling the slerped driver at
    // 1/fps reproduces what the live door computes, to the frame.
    const keyAt = (k: number): Quat =>
      quatNormalize({
        x: driverTrack.values[k * 4]!,
        y: driverTrack.values[k * 4 + 1]!,
        z: driverTrack.values[k * 4 + 2]!,
        w: driverTrack.values[k * 4 + 3]!,
      });
    const t0 = driverTrack.times[0]!;
    const tEnd = driverTrack.times[driverTrack.times.length - 1]!;
    // The grid is 1/fps EXACTLY, then the clamped endpoint — never a rounded
    // count stretched over the span (Codex P2, #103 round five: a 0.75 s span
    // at 30 fps sampled every ~32.6 ms instead of the declared 33.3 ms, so
    // the helper was still evaluated off the live door's timebase). A float
    // collision between the last grid step and the endpoint dedupes, keeping
    // times strictly ascending for the ClipData gate.
    // Driver times are float32; the grid is float64 — a microsecond guard
    // keeps a grid step that lands a few ULPs shy of the endpoint from
    // producing a duplicate final sample.
    const GRID_EPS = 1e-6;
    const sampleTimes: number[] = [t0];
    if (driverTrack.times.length > 1) {
      for (let k = 1; t0 + k / clip.fps < tEnd - GRID_EPS; k += 1) {
        const t = t0 + k / clip.fps;
        // Dedupe in the NARROWED precision the track will actually store
        // (Codex P2, #103 round six): at large time magnitudes distinct
        // float64 grid points collapse to one float32 — [1000000, 1000001]
        // at 30 fps stores 1000000.0625 twice — and a duplicated time
        // violates the ClipData strict-ascent gate downstream.
        if (Math.fround(t) > Math.fround(sampleTimes[sampleTimes.length - 1]!)) sampleTimes.push(t);
      }
      if (Math.fround(tEnd) > Math.fround(sampleTimes[sampleTimes.length - 1]!)) sampleTimes.push(tEnd);
      else if (sampleTimes.length > 1) sampleTimes[sampleTimes.length - 1] = tEnd;
    }
    const steps = sampleTimes.length;
    const times = new Float32Array(steps);
    const values = new Float32Array(steps * 4);
    const leaves = opts.includeLeafTranslations ? hinge.leaves : [];
    const leafValues = new Map(leaves.map((l) => [l.name, new Float32Array(steps * 3)] as const));
    let seg = 0;
    for (let k = 0; k < steps; k += 1) {
      const t = sampleTimes[k]!;
      while (seg < driverTrack.times.length - 2 && driverTrack.times[seg + 1]! < t) seg += 1;
      const ta = driverTrack.times[seg]!;
      const tb = driverTrack.times[Math.min(seg + 1, driverTrack.times.length - 1)]!;
      const q =
        tb <= ta ? keyAt(seg) : quatSlerp(keyAt(seg), keyAt(Math.min(seg + 1, driverTrack.times.length - 1)), Math.min(1, Math.max(0, (t - ta) / (tb - ta))));
      const reading = hingeBendAngle(hinge.profile, driverBind, q);
      const out = evaluateHingeCorrectives(hinge.profile, reading.thetaDeg);
      const rot = out.helpers.find((h) => h.name === hinge.helper)?.rotation;
      if (rot === undefined) {
        throw new HingeBakeError(`evaluator emitted no rotation for "${hinge.helper}" — profile/helper mismatch`);
      }
      const local = quatMultiply(helperBind, rot);
      times[k] = t;
      values[k * 4] = local.x;
      values[k * 4 + 1] = local.y;
      values[k * 4 + 2] = local.z;
      values[k * 4 + 3] = local.w;
      for (const leaf of leaves) {
        const delta = out.helpers.find((h) => h.name === leaf.name)?.translation;
        if (delta === undefined) {
          throw new HingeBakeError(`evaluator emitted no translation for leaf "${leaf.name}" — profile/leaf mismatch`);
        }
        const arr = leafValues.get(leaf.name)!;
        // Normalized delta × the character's measured segment length, over the
        // leaf's bind-local rest — the same sum the live door computes.
        arr[k * 3] = leaf.bindLocalPosition.x + delta.x * leaf.scaleWorld;
        arr[k * 3 + 1] = leaf.bindLocalPosition.y + delta.y * leaf.scaleWorld;
        arr[k * 3 + 2] = leaf.bindLocalPosition.z + delta.z * leaf.scaleWorld;
      }
    }
    added.push({
      target: hinge.helper,
      path: 'rotation',
      times,
      values,
    });
    for (const leaf of leaves) {
      added.push({
        target: leaf.name,
        path: 'translation',
        times: new Float32Array(times),
        values: leafValues.get(leaf.name)!,
      });
    }
  }
  if (added.length === 0) return clip;
  return { ...clip, tracks: [...clip.tracks, ...added] };
}
